import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import "@google/model-viewer";
import './../styles/editor.css';
import {
  Link,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import level2Data from "../data/level2.json";
import level3Data from "../data/level3.json";
import level4Data from "../data/level4.json";
import level5Data from "../data/level5.json";
import { getCameraOrbit, getCameraTarget, getFieldOfView, getModelName, organNames } from "../helpers";
import axios from "axios";
import { CameraContext } from "../contexts/CameraContext";
import useAuth from "../hooks/useAuth";
import RichPreview from "./RichPreview";
import { ImageContext } from "../contexts/ImageContext";
import { GalleryContentContext } from "../contexts/GalleryContentContext";
import EditOrganMenuModal from "../modal/EditOrganMenuModal";
import { Material } from "@babylonjs/core";
import TiptapRichPreview from "./RichPreview/TiptapRichPreview";
import CryptoJS from "crypto-js";


function TipTapContents() {
  const { level1, level2, level3, level4, level5 } = useParams();
  const [progress, setProgress] = useState(0);
  const [showProgress, setShowProgress] = useState(false);
  const [presignedUrl, setPresignedUrl] = useState("");
  const modelRef = useRef(null);
  const buttonsRef = useRef(null);
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [contentArr, setContentArr] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOverlay, setIsOverlay] = useState(user.isLoggedIn);
  const [isModelOverlay, setIsModelOverlay] = useState(false);
  const [showContent, setShowContent] = useState(true);
  const [showModel, setShowModel] = useState(true);
  const { imageDispatch } = useContext(ImageContext);
  const { hash } = useLocation();
  const [hasMaterials, setHasMaterials] = useState(false);
  const [hasModelDetail, setHasModelDetail] = useState(false);
  const [editedOrganMenu, setEditedOrganMenu] = useState(false);
  const [pelvisFor, setPelvisFor] = useState("Male Pelvis");
  const [contentList, setContentList] = useState([]);
  const [error, setError] = useState("");
  const [popupImages, setPopupImages] = useState([]);
  const [popupIndex, setPopupIndex] = useState(-1);
  const menuWrapperRef = useRef(null);
  const { galleryContentShow, setGalleryContentShow } = useContext(
    GalleryContentContext
  );
  const [showEditOrganModal, setShowEditOrganModal] = useState({
    show: false, // initial values set to false and null
    id: null,
  });
  const lastLoadedModelRef = useRef(null);
  const [imageMap, setImageMap] = useState({});
  const initialValue = useMemo(
    () => [
      {
        type: "paragraph",
        children: [{ text: "Loading..." }],
      },
    ],
    []
  );
  const findContent = (data, level5) => {
    return data.find((ele) => {
      return ele.level5 === level5;
    });
  };


  const MODEL_DECRYPT_KEY = process.env.REACT_APP_MODEL_DECRYPT_KEY;
  
    async function decryptArrayBuffer(buffer) {
  
    const wordArray = CryptoJS.lib.WordArray.create(new Uint8Array(buffer));
  
    const iv = CryptoJS.lib.WordArray.create(
      wordArray.words.slice(0, 4)
    );
  
    const encrypted = CryptoJS.lib.WordArray.create(
      wordArray.words.slice(4)
    );
  
    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext: encrypted },
      CryptoJS.enc.Utf8.parse(MODEL_DECRYPT_KEY),
      {
        iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      }
    );
  
    const typedArray = new Uint8Array(decrypted.sigBytes);
  
    for (let i = 0; i < decrypted.sigBytes; i++) {
      typedArray[i] =
        (decrypted.words[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff;
    }
  
    return typedArray.buffer;
  }
  async function loadModelEnc(modelName) {
  
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/model/${modelName}`
    );
  
    const { url } = await res.json();
  
    const encryptedBuffer = await fetch(url).then(r => r.arrayBuffer());
  
    const decryptedBuffer = await decryptArrayBuffer(encryptedBuffer);
  
    const blob = new Blob([decryptedBuffer], { type: "model/gltf-binary" });
  
    const blobUrl = URL.createObjectURL(blob);
  
    setPresignedUrl(blobUrl);
  }
  
  async function loadModel(filename) {
    setPresignedUrl("");
  
    const cache = await caches.open("models-v1");
  
    // 1️⃣ Check cache first
    const cached = await cache.match(filename);
  
    if (cached) {
      setShowProgress(true);
      setProgress(0);
      console.log("✅ Loaded model from cache");
  
      const blob = await cached.blob();
  
      setProgress(1);
  
      return URL.createObjectURL(blob);
    }
  
    console.log("⬇ Downloading model...");
  
    setShowProgress(true);
    setProgress(0);
  
    // 2️⃣ Get metadata
    const metaRes = await fetch(
      `${process.env.REACT_APP_API_URL}/model-meta/${filename}`
    );
  
    if (!metaRes.ok) {
      throw new Error("Failed to get model metadata");
    }
  
    const { size } = await metaRes.json();
  
    const chunkSize = 4 * 1024 * 1024; // 4MB
    const maxParallel = 4;
  
    const totalChunks = Math.ceil(size / chunkSize);
    const chunks = new Array(totalChunks);
  
    let index = 0;
    let downloaded = 0;
  
    async function worker() {
  
      while (true) {
  
        const current = index++;
  
        if (current >= totalChunks) break;
  
        const start = current * chunkSize;
        const end = Math.min(start + chunkSize - 1, size - 1);
  
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/model-chunk/${filename}?start=${start}&end=${end}`
        );
  
        if (!res.ok) {
          throw new Error(`Chunk ${current} failed (${res.status})`);
        }
  
        const buffer = await res.arrayBuffer();
  
        chunks[current] = buffer;
  
        downloaded += buffer.byteLength;
  
        setProgress(downloaded / size);
      }
    }
  
    await Promise.all(
      Array.from({ length: maxParallel }, () => worker())
    );
  
    const blob = new Blob(chunks, {
      type: "model/gltf-binary"
    });
  
    // 3️⃣ Save complete GLB into browser cache
    await cache.put(
      filename,
      new Response(blob, {
        headers: {
          "Content-Type": "model/gltf-binary"
        }
      })
    );
  
    console.log("✅ Model cached");
  
    return URL.createObjectURL(blob);
  }

  useEffect(() => {
    const url = process.env.REACT_APP_API_URL + "/get-slate-content";
    fetchContent(url);
  }, [level1, level2, level3, level4]);

  useEffect(() => {
    const removeHashCharacter = (str) => {
      const result = str.slice(1);
      return result;
    };
    const scroll = document.getElementById(removeHashCharacter(hash));
    if (scroll) {
      scroll.scrollIntoView();
    }
  }, [contentArr]);

  function manageMaterialFromModelAndDB(modelMaterials, dbMaterials) {
    if (!Array.isArray(modelMaterials) || !Array.isArray(dbMaterials)) {
      return [];
    }

    const modelMaterialMap = {};
    modelMaterials.forEach((mat) => {
      modelMaterialMap[mat.name] = mat;
    });

    const sortedDBMaterials = [...dbMaterials].sort(
      (a, b) => a.order - b.order
    );

    return sortedDBMaterials
      .map(dbMat => {
        const modelMat = modelMaterialMap[dbMat.originalName];
        if (!modelMat) return null;

        modelMat.displayName = dbMat.displayName;
        modelMat.originalName = dbMat.originalName;
        modelMat.order = dbMat.order;
        modelMat.visible = modelMat.visible ?? true;

        return modelMat;
      })
      .filter(Boolean);
  }

  /* useEffect(() => {
    if (
      !hasMaterials ||
      !hasModelDetail ||
      !modelRef.current?.model?.materials
    ) {
      return;
    }

    const rawMaterials = modelRef.current.model.materials;
    const materials = Array.isArray(rawMaterials)
      ? rawMaterials
      : Object.values(rawMaterials || {});

    const dbMaterials = hasModelDetail.materials || [];

    // console.log("GLB mats:", materials.length);
    // console.log("DB mats:", dbMaterials.length);

    if (!dbMaterials.length) {
      loadOrganMenuFromDB(materials);
      updateMaterialsIfMissing(hasModelDetail, materials);
      return;
    }

    const orderedModelMaterials = manageMaterialFromModelAndDB(
      materials,
      dbMaterials
    );

    loadOrganMenuFromDB(
      orderedModelMaterials.length
        ? orderedModelMaterials
        : materials
    );

  }, [hasMaterials, hasModelDetail]);
 */
  useEffect(() => {
    setHasMaterials(false);

    if (buttonsRef.current) {
      buttonsRef.current.innerHTML = "";
    }
  }, [level2, level3, level4, pelvisFor]);

  async function getModelDetail(level2, level3, level4) {
    /* 4️⃣ Prepare payload for DB */
    let payload = {
      level1: level1,
      level2: level2,
      level3: level3,
      level4: level4
    };
    if (level2 === "pelvis-and-perineum") {
      if (pelvisFor === "Male Pelvis") {
        payload.level2 = "male-reproductive-system";
      } else if (pelvisFor === "Female Pelvis") {
        payload.level2 = "female-reproductive-system";
      }
    }
    /* 5️⃣ Get model Detail */
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/get-glb-model-detail`,
      payload
    );

    return response.data; // 🔑 IMPORTANT

  }

  const fetchModelDetail = async () => {
    try {
      const modelDetail = await getModelDetail(level2, level3, level4);

      if (!modelDetail || modelDetail.file_name === "coming-soon" || modelDetail.file_name === "") {
        setShowModel(false);
        return null;
      }

      setHasModelDetail(modelDetail);
      setShowModel(true);

      // 🔐 presign only once per model
      if (lastLoadedModelRef.current !== modelDetail.file_name) {
        lastLoadedModelRef.current = modelDetail.file_name;

        if (process.env.NODE_ENV === "development") {
          setPresignedUrl(`/${modelDetail.file_name}`);
          // getModelPresignUrl(modelDetail.file_name);
          // const url = await loadModel(modelDetail.file_name);
          // setPresignedUrl(url);
        } else {
          getModelPresignUrl(modelDetail.file_name);
          // const url = await loadModel(modelDetail.file_name);
          // setPresignedUrl(url);
        }
      }

      /* if (lastLoadedModelRef.current !== modelDetail.file_name) {
        lastLoadedModelRef.current = modelDetail.file_name;
        getModelPresignUrl(modelDetail.file_name); // 🔥 only once
      } */

      return modelDetail;
    } catch (error) {
      console.error("Failed to fetch model detail", error);
      return null;
    }
  };

  async function updateMaterialsIfMissing(modelDetail, materials) {
    if (!materials?.length) return;
    if (!modelDetail) {
      modelDetail = await fetchModelDetail();
    }
    // console.log("updateMaterialsIfMissing modelDetail", modelDetail);

    if (modelDetail.materials.length <= 0) {
      const materialConfig = materials.map((m, order) => ({
        originalName: m.name,
        displayName: m.name,
        order,
      }));

      const payloadUpdatedMaterial = {
        _id: modelDetail.id,
        materials: materialConfig,
      };

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/update-glb-model-material`,
          payloadUpdatedMaterial
        );
        // console.log("Updated materials in DB:", response.data);
        const orderedModelMaterials = manageMaterialFromModelAndDB(
          materials,
          response.data.materials
        );
        setEditedOrganMenu(orderedModelMaterials);
        loadOrganMenuFromDB(orderedModelMaterials);
      } catch (err) {
        console.error("Failed to update materials:", err);
      }
    } else {
      console.log("Already have materials:");
    }
  }

  useEffect(() => {
    fetchModelDetail();
  }, [level2, level3, level4, pelvisFor]);

  useEffect(() => {
    setEditedOrganMenu(false); // or null
  }, [level2, level3, level4]);

  const refreshOrganMenuWithMaterials = async (updatedMaterials) => {
    const modelMaterials = modelRef.current.model.materials;
    const orderedModelMaterials = manageMaterialFromModelAndDB(
      modelMaterials,
      updatedMaterials
    );

    // console.log("refreshOrganMenuWithMaterials modelMaterials", modelMaterials);
    // console.log("refreshOrganMenuWithMaterials orderedModelMaterials", orderedModelMaterials);
    // console.log("refreshOrganMenuWithMaterials orderedModelMaterials", orderedModelMaterials);

    if (!orderedModelMaterials.length) return;
    // ✅ wait for model detail to refresh
    // await fetchModelDetail();
    setEditedOrganMenu(orderedModelMaterials);
    loadOrganMenuFromDB(orderedModelMaterials);
  };

  const fetchContent = async (url) => {
    setLoading(true);
    setError("");
    setContentList([]); // ✅ reset immediately
    imageDispatch({ type: "clear" });

    try {
      const payload = {
        level1,
        level2,
        level3,
        level4,
        level5: level5 || "",
      };

      const response = await axios.post(url, payload, {
        headers: {
          Authorization: "Bearer " + user?.jwtToken,
        },
      });

      if (!response.data || !response.data.length) {
        setError("No data available");
        return;
      }

      let recordsToDisplay = level5
        ? response.data.filter(r => r.level5 === level5)
        : response.data;

      if (!recordsToDisplay.length) {
        setError("No content found");
        return;
      }

      // ✅ Parse JSON
      const parsedList = recordsToDisplay
        .filter(rec => rec.json_content)
        .map(rec => {
          let parsed = null;
          try {
            parsed =
              typeof rec.json_content === "string"
                ? JSON.parse(rec.json_content)
                : rec.json_content;
          } catch (e) {
            console.error("JSON parse error:", e);
          }
          return { ...rec, parsedContent: parsed };
        });

      // ✅ Apply level4 filtering
      let filteredList = parsedList;
      const level4Key = `${level1}/${level2}/${level3}/${level4}`;
      const level4Config = level4Data[level4Key];

      if (level4Config?.children?.length) {
        const childSlugs = level4Config.children.map(c => c.slug);

        filteredList = parsedList
          .filter(item => childSlugs.includes(item.level5))
          .sort(
            (a, b) =>
              childSlugs.indexOf(a.level5) -
              childSlugs.indexOf(b.level5)
          );
      }

      // ✅ Extract image IDs
      const imageIds = [];

      const traverse = (node) => {
        if (!node) return;

        if (node.type === "imageBlock" && node.attrs?.images) {
          node.attrs.images.forEach(img => {
            if (img?.id) imageIds.push(img.id);
          });
        }

        node.content?.forEach(traverse);
      };

      filteredList.forEach(item => traverse(item.parsedContent));

      const uniqueIds = [...new Set(imageIds)];

      // ✅ If no images → just set content
      if (!uniqueIds.length) {
        setContentList(filteredList);
        return;
      }

      try {
        const token = user?.jwtToken || localStorage.getItem("token");

        const res = await axios.post(
          process.env.REACT_APP_API_URL + "/get-content-images",
          { imageIds: uniqueIds },
          {
            headers: { Authorization: "Bearer " + token },
          }
        );

        if (!res.data || !res.data.length) {
          // ✅ fallback if no images returned
          setContentList(filteredList);
          return;
        }

        const imageMap = {};
        res.data.forEach(img => {
          if (img?._id) imageMap[img._id] = img;
        });

        const updateImages = (node) => {
          if (!node) return;

          if (node.type === "imageBlock" && node.attrs?.images) {
            node.attrs.images = node.attrs.images.map(img => {
              const updated = imageMap[img.id];
              return updated
                ? {
                  ...img,
                  url: updated.url || img.url || "",
                  label: updated.label || img.label || "",
                  description:
                    updated.description || img.description || "",
                }
                : img;
            });
          }

          node.content?.forEach(updateImages);
        };

        filteredList.forEach(item =>
          updateImages(item.parsedContent)
        );

        setContentList([...filteredList]);

      } catch (err) {
        console.error("Image API error:", err);
        setContentList(filteredList); // ✅ fallback
      }

    } catch (err) {
      console.error(err);
      setError("Error loading content");
    } finally {
      setLoading(false); // ✅ always runs
    }
  };
  const onLoad = async () => {
    setCameraOrbit(getCameraOrbit(level3));
    setCameraTarget(getCameraTarget(level3));
    setFieldOfView(getFieldOfView(level3));
    setShowProgress(false);
    setProgress(0);

    const model = modelRef.current;
    if (!model?.model) return;

    const rawMaterials = model.model.materials;
    const materials = Array.isArray(rawMaterials)
      ? rawMaterials
      : Object.values(rawMaterials || {});

    setHasMaterials(materials.length > 0);

    // 🔥 Fetch latest model detail
    const modelDetail = await getModelDetail(level2, level3, level4);
    if (!modelDetail) return;

    setHasModelDetail(modelDetail);

    const dbMaterials = modelDetail.materials || [];

    if (!dbMaterials.length) {
      loadOrganMenuFromDB(materials);
      updateMaterialsIfMissing(modelDetail, materials);
      return;
    }

    const ordered = manageMaterialFromModelAndDB(
      materials,
      dbMaterials
    );

    loadOrganMenuFromDB(
      ordered.length ? ordered : materials
    );
  };


  const onProgress = (e) => {
    if (e.detail.totalProgress < 1) {
      setShowProgress(true);
      setProgress(e.detail.totalProgress);
    }
  };
  const onRotate = (e) => {
    // console.log("CameraOrbit: ", modelRef.current.getCameraOrbit())
    // console.log("CameraTarget: ", modelRef.current.getCameraTarget())
    // console.log("FieldOfView: ", modelRef.current.getFieldOfView())
  }
  function getLabelText(matName) {
    return matName
      .replace(/^adskMat/i, "")                // remove adskMat prefix
      .replace(/_?BaseColor\d*/gi, "")         // remove BaseColor + numbers
      .replace(/_?Base_Coloruu\d*/gi, "")         // remove BaseColor + numbers
      .replace(/_?Color_mapehg\d*/gi, "")         // remove BaseColor + numbers
      .replace(/_?4t54\d*/gi, "")         // remove BaseColor + numbers
      .replace(/_?Bigt\d*/gi, "")         // remove BaseColor + numbers
      .replace(/^M_?17_*/i, "")                // remove M_17 prefix
      .replace(/\d+$/g, "")              // 🔥 remove trailing numbers (5, 7, etc.)
      .replace(/_/g, " ")                      // replace underscores with spaces
      .replace(/\s+/g, " ")                    // clean extra spaces
      .trim();
  }

  function loadOrganMenuFromDB(materials) {
    // if (level3 == "liver" || level3 == "stomach") {
    if (!materials) return;
    if (!buttonsRef.current) return;
    // if (!modelRef.current?.model) return;

    // const materials = modelRef.current.model.materials;

    // 🔴 Clear old buttons
    buttonsRef.current.innerHTML = "";

    materials.forEach((mat, index) => {
      // console.log("mat", mat);
      let labelText = mat.displayName || `Material ${index}`;
      let visible = true;

      if (!mat._originalBaseColor) {
        if (mat.pbrMetallicRoughness) {
          // If baseColorFactor exists → clone it
          if (mat.pbrMetallicRoughness.baseColorFactor) {
            mat._originalBaseColor = [
              ...mat.pbrMetallicRoughness.baseColorFactor
            ];
          } else {
            // Force create baseColorFactor
            mat.pbrMetallicRoughness.setBaseColorFactor([1, 1, 1, 1]);
            mat._originalBaseColor = [1, 1, 1, 1];
          }
        }
      }

      /* ROW */
      const row = document.createElement("div");
      row.className =
        "d-flex align-items-center justify-content-between w-100 py-2";

      /* LABEL */
      const label = document.createElement("span");
      label.className = "text-green1 fw-bold menu-span-lable";
      label.textContent = labelText;

      /* RIGHT TOGGLE */
      const right = document.createElement("div");
      right.className = "d-flex align-items-center gap-2 pointer";

      const toggleImg = document.createElement("img");
      toggleImg.src = "/images/show-toggle.png";
      toggleImg.style.width = "3.5rem";

      right.appendChild(toggleImg);

      /* TOGGLE CLICK */
      right.onclick = () => {
        visible = !visible;
        // console.log(mat.originalName);
        if (mat.originalName === "adskMatPeritonum") {
          mat.setAlphaMode("BLEND");
          mat.transparencyMode = Material.MATERIAL_ALPHABLEND;
          mat.alpha = 0.4;
        } else {
          mat.setAlphaMode("MASK");
          mat.setAlphaCutoff(0.5);
        }

        const [r, g, b, a] = mat._originalBaseColor;

        mat.pbrMetallicRoughness?.setBaseColorFactor([
          r,
          g,
          b,
          visible ? a : 0
        ]);

        toggleImg.src = visible
          ? "/images/show-toggle.png"
          : "/images/hidetoggleicon.png";
      };

      row.appendChild(label);
      row.appendChild(right);
      buttonsRef.current.appendChild(row);
    });

    /* MENU DEFAULT STATE */
    buttonsRef.current.classList.remove("open");

    /* TOGGLE MENU BUTTON */
    const toggleBtn = document.getElementById("variantToggleBtn");
    if (toggleBtn) {
      toggleBtn.onclick = (e) => {
        e.stopPropagation();
        buttonsRef.current.classList.toggle("open");
      };
    }
    // }

  }

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!buttonsRef.current || !menuWrapperRef.current) return;

      // click happened outside menu + button
      if (!menuWrapperRef.current.contains(e.target)) {
        buttonsRef.current.classList.remove("open");
      }
    };

    document.addEventListener("click", handleOutsideClick);

    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    if (modelRef.current) {
      modelRef.current.addEventListener("load", onLoad);
      modelRef.current.addEventListener("progress", onProgress);
      modelRef.current.addEventListener("camera-change", onRotate);
    }
    return () => {
      if (modelRef.current) {
        modelRef.current.removeEventListener("load", onLoad);
        modelRef.current.removeEventListener("progress", onProgress);
        modelRef.current.removeEventListener("camera-change", onRotate);
      }
    };
  }, [modelRef, level2, level3, level4, pelvisFor]);

  const getModelPresignUrl = (modelName) => {
    setProgress(0);
    setShowProgress(true);
    modelRef.current.showPoster();
    let URL = process.env.REACT_APP_API_URL + "/get-upload-model-presign-url";
    let payload = {
      model_name: `${modelName}`,
    };
    axios
      .post(URL, payload, {
        headers: { Authorization: "Bearer " + user.jwtToken },
      })
      .then((res) => {
        if (res && res.data) {
          setPresignedUrl(res.data);
        }
      })
      .catch((err) => console.log(err));
  };

  const setCamera = (target, orbit) => {
    if (modelRef.current) {
      modelRef.current.cameraTarget = target;
      modelRef.current.cameraOrbit = orbit;
      // modelRef.current.fieldOfView = "45deg";
    }
  };
  const setCameraTarget = (target) => {
    if (modelRef.current) {
      modelRef.current.cameraTarget = target;
    }
  };
  const setCameraOrbit = (orbit) => {
    if (modelRef.current) {
      modelRef.current.cameraOrbit = orbit;
    }
  };
  const setFieldOfView = (fov) => {
    if (modelRef.current) {
      modelRef.current.fieldOfView = fov;
    }
  };
  const handleOverlayClose = async () => {
    setIsOverlay(true);
    let URL = process.env.REACT_APP_API_URL + "/update-is-loggedIn";
    await axios
      .get(URL, { headers: { Authorization: "Bearer " + user.jwtToken } })
      .then((res) => {
        let userObj = { isLoggedIn: true };
        updateUser(userObj, () => { });
      })
      .catch((err) => console.log(err));
  };
  const renderModelOverlayScreen = () => {
    return (
      <div
        className={"position-absolute d-block h-100 w-50 "}
        style={{ zIndex: 100, backgroundColor: "rgba(0,0,0, 0.6)" }}
      >
        <a
          className="d-flex justify-content-end p-2"
          onClick={() => setIsModelOverlay(!isModelOverlay)}
        >
          <img
            src="/images/close.png"
            alt=""
            style={{ width: "2.5rem", cursor: "pointer" }}
          />
        </a>
        <div className="d-flex flex-column justify-content-center align-items-center h-75">
          <div className="pb-4">
            <div className="d-flex justify-content-center align-items-center">
              <div className="row">
                <div className="col">
                  <img
                    src="/images/click.svg"
                    alt=""
                    className="p-4"
                    style={{ width: "8rem" }}
                  />
                </div>
                <div className="col">
                  <img
                    src="/images/drag.svg"
                    alt=""
                    className="p-4"
                    style={{ width: "8rem" }}
                  />
                </div>
              </div>
            </div>
            <div className="text-center text-white fw-bold fs-5">
              Right click and drag to reposition the model.
              <br />
              Left click and drag to rotate the model.
            </div>
          </div>
          <div className="">
            <div className="d-flex justify-content-center align-items-center">
              <div className="row">
                <div className="col">
                  <img
                    src="/images/scroll.svg"
                    alt=""
                    className="p-4"
                    style={{ width: "7rem" }}
                  />
                </div>
                <div className="col">
                  <img
                    src="/images/pinch.svg"
                    alt=""
                    className="p-4"
                    style={{ width: "8rem" }}
                  />
                </div>
              </div>
            </div>
            <div className="text-center text-white fw-bold fs-5">
              Scroll or pinch and drag to zoom in and out.
            </div>
          </div>
        </div>
      </div>
    );
  };
  const renderOverlayScreen = () => {
    return (
      <div
        className={"h-100 position-fixed top-0 start-0 d-block  w-100"}
        style={{ zIndex: 100, backgroundColor: "rgba(0,0,0, 0.6)" }}
      >
        <div className="d-flex flex-column justify-content-center align-items-center h-100">
          <div className="pb-4">
            <div className="d-flex justify-content-center align-items-center">
              <div className="row">
                <div className="col">
                  <img
                    src="/images/click.svg"
                    alt=""
                    className="p-4"
                    style={{ width: "8rem" }}
                  />
                </div>
                <div className="col">
                  <img
                    src="/images/drag.svg"
                    alt=""
                    className="p-4"
                    style={{ width: "8rem" }}
                  />
                </div>
              </div>
            </div>
            <div className="text-center text-white fw-bold fs-5">
              Right click and drag to reposition the model.
              <br />
              Left click and drag to rotate the model.
            </div>
          </div>
          <div className="">
            <div className="d-flex justify-content-center align-items-center">
              <div className="row">
                <div className="col">
                  <img
                    src="/images/scroll.svg"
                    alt=""
                    className="p-4"
                    style={{ width: "7rem" }}
                  />
                </div>
                <div className="col">
                  <img
                    src="/images/pinch.svg"
                    alt=""
                    className="p-4"
                    style={{ width: "8rem" }}
                  />
                </div>
              </div>
            </div>
            <div className="text-center text-white fw-bold fs-5">
              Scroll or pinch and drag to zoom in and out.
            </div>
            <a
              className="d-flex justify-content-center mt-5"
              onClick={handleOverlayClose}
            >
              <img
                src="/images/close.png"
                alt=""
                style={{ width: "3rem", cursor: "pointer" }}
              />
            </a>
          </div>
        </div>
      </div>
    );
  };
  const editOrganMenuModelClick = (id) => {
    setShowEditOrganModal({
      show: true,
      id,
    });
  };

  const handlePelvisModelChange = (pelvisFor) => {
    setPelvisFor(pelvisFor);
    setShowModel(false);   // hide old model immediately
    setHasMaterials(false);
    lastLoadedModelRef.current = null;

    // optional: clear old menu instantly for UI feedback
    if (buttonsRef.current) {
      buttonsRef.current.innerHTML = "";
    }

    console.log("Pelvis changed to:", pelvisFor);
  };

  const InlineChromiumBugfix = () => (
    <span
      contentEditable={false}
      style={{
        fontSize: "0"
      }}
    >
      ${String.fromCodePoint(160) /* Non-breaking space */}
    </span>
  )


  const ImageLinkComponent = ({ attributes, children, element }) => {
    // console.log(attributes);
    const { level1, level2, level3, level4 } = useParams();
    return (
      <Link
        {...attributes}
        href="#"
        className='pointer fw-bold'
        style={{ color: "#d56d10" }}
      // state={{ imageId: element.imageId }}
      // to={`/${level1}/${level2}/${level3}/${level4}/image/${element.imageId}`}
      >
        <InlineChromiumBugfix />
        {children}
        <InlineChromiumBugfix />
      </Link>
    )
  }


  const markMap = {
    bold: (c) => <strong>{c}</strong>,
    italic: (c) => <em>{c}</em>,
    underline: (c) => <span style={{ textDecoration: "underline" }}>{c}</span>,
    strike: (c) => <s>{c}</s>,
    code: (c) => <code>{c}</code>,

    link: (c, attrs) => (
      <a
        href={attrs?.href}
        target={attrs?.target || "_blank"}
        rel={attrs?.rel || "noopener noreferrer"}
      >
        {c}
      </a>
    ),

    contentLink: (c, attrs) => {
      let path = attrs?.href || "";
      path = path.replace("torso", level1);
      const absolute = path.startsWith("/") ? path : `/${path}`;
      // return <a className="text-green6 pointer fw-bold" href={absolute}>{c}</a>;
      return <Link
        className="text-green6 pointer fw-bold"
        to={`${absolute}`}
      >
        {c}
      </Link>
    },

    imageLink: (c, attrs) => {
      const imageId = attrs?.imageId;
      if (!imageId) return c;

      return (
        <Link
          className="pointer fw-bold"
          style={{ color: "#d56d10", textDecoration: "underline" }}
          state={{ imageId }}
          to={`/${level1}/${level2}/${level3}/${level4}/image/${imageId}`}
        >
          {c}
        </Link>
      );
    },
    label: (c) => <span className="custom-label">{c}</span>,


  };

  const applyMarks = (textNode) => {
    let content = textNode.text;

    if (!textNode.marks) return content;

    return textNode.marks.reduce((acc, mark) => {
      const fn = markMap[mark.type];
      return fn ? fn(acc, mark.attrs) : acc;
    }, content);
  };

  const renderText = (node) => {
    let content = (node && node.text) || "";
    if (node && node.marks) {
      node.marks.forEach((mark, index) => {
        const renderer = markMap[mark.type];
        // console.log("renderer",renderer)
        if (renderer) {
          content = (
            <React.Fragment key={index}>
              {renderer(content, mark.attrs)}
            </React.Fragment>
          );
        }
      });
    }
    return content;
  };

  // const renderNodes = (node) => {
  //     if (!node) return null;

  //     if (Array.isArray(node)) {
  //         return node.map((child, index) => (
  //             <React.Fragment key={index}>{renderNodes(child)}</React.Fragment>
  //         ));
  //     }

  //     switch (node.type) {
  //         case "doc":
  //             return renderNodes(node.content);
  //         case "paragraph":
  //             return <p>{renderNodes(node.content)}</p>;
  //         case "heading": {
  //             const Tag = `h${node.attrs?.level || 1}`;
  //             return <Tag>{renderNodes(node.content)}</Tag>;
  //         }
  //         case "bulletList":
  //             return <ul>{renderNodes(node.content)}</ul>;
  //         case "orderedList":
  //             return (
  //                 <ol
  //                     start={node.attrs?.start || 1}
  //                     type={node.attrs?.type || "1"}
  //                     className={`list-type-${node.attrs?.type || "1"}`}
  //                 >
  //                     {renderNodes(node.content)}
  //                 </ol>
  //             );
  //         case "listItem":
  //             return <li className="li-p">{renderNodes(node.content)}</li>;
  //         case "text":
  //             return renderText(node);
  //         case "hardBreak":
  //             return <br />;
  //         case "horizontalRule":
  //             return <hr />;
  //         case "blockquote":
  //             return <blockquote style={{ borderLeft: "4px solid #ccc", paddingLeft: "15px", margin: "15px 0", fontStyle: "italic" }}>{renderNodes(node.content)}</blockquote>;
  //         case "imageBlock":
  //             return (
  //                 <div className="row row-cols-auto">
  //                     {(node.attrs?.images || []).map((image, idx) => {
  //                         if (!image || !image.id) return null;
  //                         return (
  //                           <div className='col mb-3'>
  //                             <div className="d-flex border border-gray3 overflow-hidden border-5 bg-white rounded position-relative" style={{ height: "10rem" }} id={image.id}>
  //                                 <img src={`${process.env.REACT_APP_MEDIA_URL}/${image.url}`} className="h-100" />
  //                                 <Link
  //                                     className='stretched-link pointer'
  //                                     state={{ imageId: image.id }}
  //                                     to={`/${level1}/${level2}/${level3}/${level4}/image/${image.id}`}
  //                                 >
  //                                     <span className="text-green1 position-absolute bottom-0 end-0 me-1">
  //                                         <span className='material-icons'>fullscreen</span>
  //                                     </span>
  //                                 </Link>
  //                             </div>
  //                          </div>
  //                         );
  //                     })}
  //                 </div>
  //             );
  //         case "table":
  //             return (
  //                 <table style={{ width: "100%", borderCollapse: "collapse", margin: "10px 0", tableLayout: "fixed" }}>
  //                     <tbody>{renderNodes(node.content)}</tbody>
  //                 </table>
  //             );
  //         case "tableRow":
  //             return <tr>{renderNodes(node.content)}</tr>;
  //         case "tableCell":
  //             return <td style={{ border: "1px solid #ccc", padding: "8px" }}>{renderNodes(node.content)}</td>;
  //         case "tableHeader":
  //             return <th style={{ border: "1px solid #ccc", padding: "8px", background: "#f4f4f4" }}>{renderNodes(node.content)}</th>;
  //         default:
  //             return null;
  //     }
  // };


  const renderNodes = (node) => {
    if (!node) return null;

    if (Array.isArray(node)) {
      return node.map((n, i) => (
        <React.Fragment key={i}>{renderNodes(n)}</React.Fragment>
      ));
    }

    // ✅ Delegate complex blocks
    const richTypes = [
      "accordion",
      "accordionItem",
      "accordionTitle",
      "accordionContent",
      "image",
      "imageBlock",
      "imageList",
      "video",
    ];

    if (richTypes.includes(node.type)) {
      return <TiptapRichPreview node={node} />;
    }
    // console.log("node.type",node);
    switch (node.type) {
      case "doc":
        return renderNodes(node.content);

      case "paragraph":
        return <p>{renderNodes(node.content)}</p>;

      case "heading": {
        const Tag = `h${node.attrs?.level || 1}`;
        return <Tag>{renderNodes(node.content)}</Tag>;
      }

      case "bulletList":
        return <ul>{renderNodes(node.content)}</ul>;

      case "orderedList":
        return <ol
          start={node.attrs?.start || 1}
          type={node.attrs?.type || "1"}
          className={`list-type-${node.attrs?.type || "1"}`}
        >{renderNodes(node.content)}
        </ol>;

      case "listItem":
        return <li>{renderNodes(node.content)}</li>;

      case "text":
        return applyMarks(node);

      case "hardBreak":
        return <br />;

      case "horizontalRule":
        return <hr />;

      // ✅ KEEP TABLE HERE (simple rendering)
      case "table":
        return (
          <table className="preview-table">
            <tbody>{renderNodes(node.content)}</tbody>
          </table>
        );

      case "tableRow":
        return <tr>{renderNodes(node.content)}</tr>;

      case "tableCell":
        return (
          <td>
            {renderNodes(node.content)}
          </td>
        );
      default:
        return null;
    }
  };

  const handleNext = (e) => {
    e.stopPropagation();
    if (popupIndex < popupImages.length - 1) {
      setPopupIndex(popupIndex + 1);
    }
  };

  const handlePrev = (e) => {
    e.stopPropagation();
    if (popupIndex > 0) {
      setPopupIndex(popupIndex - 1);
    }
  };

  const popupImage = popupIndex >= 0 ? popupImages[popupIndex] : null;


  return (
    <>
      {hasModelDetail && (
        <EditOrganMenuModal
          modal={showEditOrganModal.show}
          setModal={setShowEditOrganModal}
          id={hasModelDetail.id}
          materials={editedOrganMenu ? editedOrganMenu : hasModelDetail.materials}
          onMaterialsUpdated={refreshOrganMenuWithMaterials}
        />
      )}
      {level3Data[`${level1}/${level2}/${level3}`] ? (
        ""
      ) : (
        <Navigate to="/dashboard" />
      )}
      <CameraContext.Provider
        value={{
          setCamera: setCamera,
          setCameraOrbit: setCameraOrbit,
          setCameraTarget: setCameraTarget,
          setFieldOfView: setFieldOfView,
        }}
      >
        <main className="main-section mx-4 rounded-top-3 bg-white">
          <div className="h-100">
            <div className="row bg-green4 rounded-top-3 m-0 content-header position-relative">
              <div className="position-absolute d-flex w-auto top-0 start-50 translate-middle-x mt-2 align-items-center justify-content-center text-green1">
                <Link
                  to={`/${level1}/${level2}`}
                  className="btn btn-link btn-sm text-decoration-none fw-bold"
                >
                  {organNames(level2Data, level1, level2)}
                  {/* {level2Data[`${level1}/${level2}`] ? level2Data[`${level1}/${level2}`].version + " " + level2Data[`${level1}/${level2}`].name : ""} */}
                </Link>
              </div>
              <div className="col-12 d-flex d-xl-none content-header-top"></div>
              <div className="col-6 d-flex align-items-center justify-content-start">
                <button
                  className="btn btn-sm btn-link text-decoration-none fw-bold"
                  onClick={() => navigate(-1)}
                >
                  <span className="icon-left"></span> Previous
                </button>
                <span className="text-green1 fw-bold pe-1">Copy</span>
                <div
                  onClick={() => {
                    setShowContent(!showContent);
                  }}
                >
                  <img
                    src={showContent ? "/images/show-toggle.png" : "/images/hidetoggleicon.png"}
                    alt=""
                    style={{ width: "4rem" }}
                    className="pe-2 pointer"
                  />
                </div>
                {/* <div className="btn-group">
                  <button
                    className={`btn btn-sm fw-bold ${
                      showContent ? "btn-green3a" : "btn-green1a"
                    }`}
                    onClick={() => {
                      setShowContent(!showContent);
                    }}
                  >
                    Hide content
                  </button>
                  <button
                    className={`btn btn-sm fw-bold ${
                      showContent ? "btn-green1a" : "btn-green3a"
                    }`}
                    onClick={() => {
                      setShowContent(!showContent);
                    }}
                  >
                    Show content
                  </button>
                </div> */}

                <span className="text-green1 fw-bold pe-1">Images</span>
                <div
                  onClick={() => {
                    setGalleryContentShow(!galleryContentShow);
                  }}
                >
                  <img
                    src={
                      galleryContentShow
                        ? "/images/show-toggle.png"
                        : "/images/hidetoggleicon.png"
                    }
                    alt=""
                    style={{ width: "4rem" }}
                    className="pe-2 pointer"
                  />
                </div>
              </div>
              <div className="col-6 d-flex align-items-center justify-content-end text-green1 content-header-bottom">
                <div className="dropdown me-3">
                  <div
                    role="button"
                    className="fs-7 fw-bold"
                    id="dropdownMenuButton1"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <span>
                      {level3Data[`${level1}/${level2}/${level3}`]
                        ? level3Data[`${level1}/${level2}/${level3}`].version +
                        " " +
                        level3Data[`${level1}/${level2}/${level3}`].name
                        : ""}
                    </span>{" "}
                    <span className="icon-down"></span>
                  </div>
                  <ul
                    className="dropdown-menu dropdown-menu-end"
                    aria-labelledby="dropdownMenuButton1"
                  >
                    {level2Data[`${level1}/${level2}`] &&
                      level2Data[`${level1}/${level2}`].children.map(
                        (l3, l3Index) => {
                          return (
                            <li key={l3Index}>
                              <Link
                                className={
                                  "dropdown-item fs-7 fw-bolder " +
                                  (l3.slug === level3 ? "active" : "")
                                }
                                to={`/${level1}/${level2}/${l3.slug}/${level3Data[`${level1}/${level2}/${l3.slug}`]
                                  .children[0].slug
                                  }`}
                              >
                                {l3.version} {l3.name}
                              </Link>
                            </li>
                          );
                        }
                      )}
                  </ul>
                </div>
                {level1 != "chest" && (
                  <div className="dropdown">
                  <div
                    role="button"
                    className="fs-7 fw-bold"
                    id="dropdownMenuButton2"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <span>
                      {level4Data[`${level1}/${level2}/${level3}/${level4}`]
                        ? level4Data[`${level1}/${level2}/${level3}/${level4}`]
                          .name
                        : ""}
                    </span>{" "}
                    <span className="icon-down"></span>
                  </div>
                  <ul
                    className="dropdown-menu dropdown-menu-end"
                    aria-labelledby="dropdownMenuButton2"
                  >
                    {level3Data[`${level1}/${level2}/${level3}`] &&
                      level3Data[`${level1}/${level2}/${level3}`].children.map(
                        (l4, l4Index) => {
                          return (
                            <li key={l4Index}>
                              <Link
                                className={
                                  "dropdown-item fs-7 fw-bolder " +
                                  (l4.slug === level4 ? "active" : "")
                                }
                                to={`/${level1}/${level2}/${level3}/${l4.slug}`}
                              >
                                {l4.name}
                              </Link>
                            </li>
                          );
                        }
                      )}
                  </ul>
                </div>
                )}
              </div>
            </div>
            <div
              className={
                "height-scroll bg-gray5 overflow-hidden " +
                (showContent ? "d-flex justify-content-between" : "")
              }
              style={{ position: "relative" }}
            >
              {!isOverlay && showModel ? renderOverlayScreen() : <></>}
              {isModelOverlay && renderModelOverlayScreen()}
              <div
                className={
                  "model-object bg-gray5 h-100 " +
                  (showContent ? "w-50" : "w-100")
                }
              >
                {showModel && (
                  <div className="d-flex justify-content-between align-items-start w-100 menu-main-div">
                    {/* Info icon on the left */}
                    {showModel && (
                      <div
                        className="pointer-cursor"
                        onClick={() => setIsModelOverlay(!isModelOverlay)}
                      >
                        <img
                          alt="info"
                          src="/images/info.svg"
                          style={{ width: "2.5rem" }}
                        />
                      </div>
                    )}

                    {level2 == "pelvis-and-perineum" && level3 == "pelvis" && (
                      <>
                        <div className="d-flex align-items-center justify-content-end dropdown">
                          <button
                            className="btn btn-secondary dropdown-toggle fw-bold"
                            type="button"
                            id="dropdownMenuButton2"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                          >
                            {pelvisFor}
                          </button>

                          <ul
                            className="dropdown-menu dropdown-menu-end"
                            aria-labelledby="dropdownMenuButton2"
                          >
                            <li>
                              <button
                                type="button"
                                className={`dropdown-item fs-7 fw-bolder ${pelvisFor === "Male Pelvis" ? "active" : ""
                                  }`}
                                onClick={() => handlePelvisModelChange("Male Pelvis")}
                              >
                                Male Pelvis
                              </button>
                            </li>

                            <li>
                              <button
                                type="button"
                                className={`dropdown-item fs-7 fw-bolder ${pelvisFor === "Female Pelvis" ? "active" : ""
                                  }`}
                                onClick={() => handlePelvisModelChange("Female Pelvis")}
                              >
                                Female Pelvis
                              </button>
                            </li>
                          </ul>
                        </div>
                      </>
                    )}

                    <div ref={menuWrapperRef} className="model-overlay-menu row-sm-4">
                      {/* Overlay menu */}
                      {hasMaterials && (
                        <>

                          <div className="d-flex gap-2">
                            {/* <button
                              className="toggle-btn icon-btn"
                              onClick={() => editOrganMenuModelClick(hasModelDetail.id)}
                            >
                              <i className="bi bi-pencil-square"></i>
                            </button> */}

                            <button
                              id="variantToggleBtn"
                              className="toggle-btn icon-btn"
                            >
                              <span
                                className="material-symbols-outlined"
                                style={{ fontSize: "20px" }}
                              >
                                menu
                              </span>
                            </button>
                          </div>

                          <div
                            id="variantButtons"
                            ref={buttonsRef}
                            className="model-overlay-buttons organ-menu"
                          />

                        </>
                      )}
                    </div>
                  </div>
                )}
                <div
                  className={`w-100 h-100 bg-green1 position-relative justify-content-center align-items-center ${showModel ? "d-none" : "d-flex"
                    }`}
                >
                  <div className="position-absolute z-0 w-100 h-100 coming-soon-bg1"></div>
                  <div className="position-absolute z-1 w-100 h-100 coming-soon-bg2"></div>
                  <div className="position-absolute z-2 w-100 h-100 d-flex justify-content-center align-items-center">
                    <h1 className="text-white fw-bold">Model Coming Soon</h1>
                  </div>
                </div>
                <div
                  className={`w-100 h-100 ${showModel ? "d-flex" : "d-none"}`}
                >
                  <div className="model-section">
                    <model-viewer
                      key={`${level1}-${level2}-${level3}-${level4}-${pelvisFor}`}
                      className="model-viewer"
                      camera-orbit={getCameraOrbit(level3)}
                      camera-target={getCameraTarget(level3)}
                      field-of-view={getFieldOfView(level3)}
                      src={showModel ? presignedUrl : ""}
                      environment-image="/images/hdri/neutral.hdr"
                      alt="Liver"
                      camera-controls
                      ref={modelRef}
                      interaction-prompt="none"
                    >
                      <div slot="poster" className="position-relative h-100">
                        <div className="position-absolute top-50 start-50 translate-middle"></div>
                      </div>
                      <div
                        slot="progress-bar"
                        className={
                          "position-relative h-100 " +
                          (showProgress ? "d-block" : "d-none")
                        }
                      >
                        <div className="position-absolute top-50 start-50 translate-middle">
                          <div className="lds-ring">
                            <div></div>
                            <div></div>
                            <div></div>
                            <div></div>
                          </div>
                          <div className="position-absolute top-50 start-50 translate-middle">
                            <span className="fw-bold text-secondary">
                              {Math.trunc(progress * 100)}%
                            </span>
                          </div>
                        </div>
                      </div>
                    </model-viewer>
                  </div>

                </div>
              </div>
              <div
                className={
                  "px-4 py-3 bg-white overflow-y-scroll scroller " +
                  (showContent ? "w-50" : "d-none w-0")
                }
                id="scroll"
              >
                <div className="">
                  <h4 className="fw-bold text-black pb-1">
                    {/* {level4Data[`${level1}/${level2}/${level3}/${level4}`]
                      ? `${level4Data[`${level1}/${level2}/${level3}/${level4}`]
                        .version
                      } ${level4Data[`${level1}/${level2}/${level3}/${level4}`]
                        .name
                      }`
                      : ""} */}
                      {level4Data[`${level1}/${level2}/${level3}/${level4}`]
                      ?
                      level1 == "chest"
                      ?
                      `${level3Data[`${level1}/${level2}/${level3}`]
                        .version
                      } ${level4Data[`${level1}/${level2}/${level3}/${level4}`]
                        .name
                      }`
                      : 
                      `${level4Data[`${level1}/${level2}/${level3}/${level4}`]
                        .version
                      } ${level4Data[`${level1}/${level2}/${level3}/${level4}`]
                        .name
                      }`
                      : ""}
                  </h4>
                  <div className="text-gray2 custom-scroller-content">

                    {/* ✅ Loading */}
                    {loading && (
                      <p className="card-text placeholder-glow">
                        {[...Array(8)].map((_, i) => (
                          <span key={i} className="placeholder col-12" />
                        ))}
                      </p>
                    )}

                    {/* ✅ Main Content */}
                    {!loading && (() => {

                      const level4Key = `${level1}/${level2}/${level3}/${level4}`;
                      const level4Config = level4Data[level4Key];

                      // 🔥 CASE 1: Has children (MAIN FIX)
                      if (level4Config?.children?.length > 0) {
                        return level4Config.children.map((l5, l5Index) => {

                          const content = contentList.find(
                            item =>
                              item.level1 === level1 &&
                              item.level2 === level2 &&
                              item.level3 === level3 &&
                              item.level4 === level4 &&
                              item.level5 === l5.slug
                          );

                          return (
                            <div
                              key={`${level4Key}-${l5.slug}-${l5Index}`}
                              id={l5.slug}
                              className="mb-4"
                            >
                              {/* ✅ Title */}
                              <h5 className="fw-bold text-black pb-1">
                                {l5.name}
                              </h5>

                              {/* ✅ ALWAYS show Edit */}
                              {user.role !== "Student" && (
                                <div className="d-flex justify-content-end">
                                  <Link
                                    to={`/edit/${level1}/${level2}/${level3}/${level4}/${l5.slug}`}
                                    className="btn btn-link btn-sm text-secondary"
                                  >
                                    Edit
                                  </Link>
                                </div>
                              )}

                              {/* ✅ Content OR Empty state */}
                              {content ? (
                                <div className="content">
                                  {renderNodes(content.parsedContent)}
                                </div>
                              ) : (
                                <div className="text-muted small">
                                  No content yet. Click "Edit" to add.
                                </div>
                              )}
                            </div>
                          );
                        });
                      }

                      // 🔥 CASE 2: No children (fallback)
                      return (
                        <div key={`${level4Key}-fallback`}>
                          {contentList.length > 0 ? (
                            contentList.map((item, idx) => {

                              const level4KeyItem = `${item.level1}/${item.level2}/${item.level3}/${item.level4}`;
                              const level4ConfigItem = level4Data[level4KeyItem];

                              const childConfig = level4ConfigItem?.children?.find(
                                c => c.slug === item.level5
                              );

                              const title = childConfig
                                ? childConfig.name
                                : item.level5
                                  ? item.level5
                                    .split("-")
                                    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
                                    .join(" ")
                                  : "";

                              return (
                                <div
                                  key={item._id || idx}
                                  id={item.level5}
                                  style={{ marginBottom: "40px" }}
                                >
                                  <div className="d-flex justify-content-between align-items-baseline">
                                    <h3 className="fw-bold text-dark m-0">
                                      {title}
                                    </h3>

                                    {user.role !== "Student" && (
                                      <Link
                                        to={`/edit/${item.level1}/${item.level2}/${item.level3}/${item.level4}${item.level5 ? `/${item.level5}` : ""}`}
                                        className="btn btn-link btn-sm text-secondary"
                                      >
                                        Edit
                                      </Link>
                                    )}
                                  </div>

                                  <div className="content">
                                    {renderNodes(item.parsedContent)}
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            // ✅ EMPTY fallback
                            <div className="d-flex justify-content-between align-items-baseline">
                              <h3 className="fw-bold text-dark m-0">
                              </h3>
                              {user.role !== "Student" && (
                                <Link
                                  to={`/edit/${level1}/${level2}/${level3}/${level4}${level5 ? `/${level5}` : ""}`}
                                  className="btn btn-link btn-sm text-secondary"
                                >
                                  Edit
                                </Link>
                              )}
                            </div>
                          )}
                        </div>
                      );

                    })()}

                  </div>

                  <div className="pt-3 row d-flex justify-content-center">
                    {level4Data[`${level1}/${level2}/${level3}/${level4}`] &&
                      level4Data[`${level1}/${level2}/${level3}/${level4}`]
                        .nav ? (
                      <>
                        <div className="col-12 col-xl-6 mb-2 d-flex justify-content-center justify-content-xl-start">
                          <Link
                            className="btn btn-green1 btn-sm fw-bold"
                            to={`/${level4Data[
                              `${level1}/${level2}/${level3}/${level4}`
                            ].nav[0]
                              }`}
                          >
                            <span>
                              <span className="icon-left fs-6"></span>
                              {/* {level4Data[
                                level4Data[
                                  `${level1}/${level2}/${level3}/${level4}`
                                ].nav[0]
                              ]
                                ? `${level4Data[
                                  level4Data[
                                    `${level1}/${level2}/${level3}/${level4}`
                                  ].nav[0]
                                ].version
                                } ${level4Data[
                                  level4Data[
                                    `${level1}/${level2}/${level3}/${level4}`
                                  ].nav[0]
                                ].name
                                }`
                                : ""} */}
                                {level4Data[
                                level4Data[
                                  `${level1}/${level2}/${level3}/${level4}`
                                ].nav[0]
                              ]
                                ?
                                 level4Data[
                                    `${level1}/${level2}/${level3}/${level4}`
                                  ].nav[0].includes("chest")
                                 ?
                                  `${level4Data[
                                  level4Data[
                                    `${level1}/${level2}/${level3}/${level4}`
                                  ].nav[0]
                                ].version.replace(/\.1$/, "")
                                } ${level4Data[
                                  level4Data[
                                    `${level1}/${level2}/${level3}/${level4}`
                                  ].nav[0]
                                ].name
                                }`
                                 :
                                `${level4Data[
                                  level4Data[
                                    `${level1}/${level2}/${level3}/${level4}`
                                  ].nav[0]
                                ].version
                                } ${level4Data[
                                  level4Data[
                                    `${level1}/${level2}/${level3}/${level4}`
                                  ].nav[0]
                                ].name
                                }`
                                : ""}
                            </span>
                          </Link>
                        </div>
                        <div className="col-12 col-xl-6 mb-2 d-flex justify-content-center justify-content-xl-end">
                          <Link
                            className="btn btn-green1 btn-sm fw-bold"
                            to={`/${level4Data[
                              `${level1}/${level2}/${level3}/${level4}`
                            ].nav[1]
                              }`}
                          >
                            <span>
                              {/* {level4Data[
                                level4Data[
                                  `${level1}/${level2}/${level3}/${level4}`
                                ].nav[1]
                              ]
                                ? `${level4Data[
                                  level4Data[
                                    `${level1}/${level2}/${level3}/${level4}`
                                  ].nav[1]
                                ].version
                                } ${level4Data[
                                  level4Data[
                                    `${level1}/${level2}/${level3}/${level4}`
                                  ].nav[1]
                                ].name
                                }`
                                : ""} */}
                                {level4Data[
                                level4Data[
                                  `${level1}/${level2}/${level3}/${level4}`
                                ].nav[1]
                              ]
                                ? 
                                 level4Data[
                                    `${level1}/${level2}/${level3}/${level4}`
                                  ].nav[1].includes("chest")
                                 ?
                                  `${
                                    level4Data[
                                      level4Data[
                                        `${level1}/${level2}/${level3}/${level4}`
                                      ].nav[1]
                                    ].version.replace(/\.1$/, "")
                                  }
                                ${level4Data[
                                  level4Data[
                                    `${level1}/${level2}/${level3}/${level4}`
                                  ].nav[1]
                                ].name
                                }`
                                 :
                                `${level4Data[
                                  level4Data[
                                    `${level1}/${level2}/${level3}/${level4}`
                                  ].nav[1]
                                ].version
                                } ${level4Data[
                                  level4Data[
                                    `${level1}/${level2}/${level3}/${level4}`
                                  ].nav[1]
                                ].name
                                }`
                                : ""}
                              <span className="icon-right fs-6"></span>
                            </span>
                          </Link>
                        </div>
                      </>
                    ) : (
                      ""
                    )}
                  </div>


                </div>
              </div>
              <Outlet />
            </div>
          </div>
        </main>
      </CameraContext.Provider>
    </>
  );
}

export default TipTapContents;
